import {
  isFinishSplit,
  isLineString,
  isPoint,
  isPointOnLineString,
  isStartSplit,
} from '@racemap/utilities/functions/validation';
import type { LineStringObject, PointOnLineStringObject } from '@racemap/utilities/types/geos';
import type { Immutable } from 'immer';
import type { Feature } from '../Editor';

export type SelectedFeatures = Immutable<Array<Feature>>;

export function getFeatureLabel(feature: Immutable<Feature>): string {
  const f = feature as unknown as Feature;
  const name = f.properties?.name as string | undefined;
  const typePrefix = isLineString(f)
    ? 'Track'
    : isPointOnLineString(f)
      ? 'Split'
      : isPoint(f)
        ? 'POI'
        : 'Area';
  return name || `${typePrefix} ${f.id.slice(-5)}`;
}

function isStartOrFinishSplit(feature: Immutable<Feature>, allFeatures: SelectedFeatures): boolean {
  const f = feature as unknown as PointOnLineStringObject;
  if (!isPointOnLineString(f)) return false;
  if (isStartSplit(f)) return true;
  const lineStringId = f.properties?.lineStringId;
  const lineString = [...allFeatures].find(
    (feat) => (feat as unknown as Feature).id === lineStringId,
  );
  if (lineString == null || !isLineString(lineString as unknown as Feature)) return false;
  return isFinishSplit(f, lineString as unknown as LineStringObject);
}

export function hasStartOrFinishSplitInSelection(
  selectedFeatures: SelectedFeatures,
  allFeatures: SelectedFeatures,
): boolean {
  return [...selectedFeatures].some((f) => isStartOrFinishSplit(f, allFeatures));
}

export function getRemoveTitle(features: SelectedFeatures, allFeatures: SelectedFeatures): string {
  if (features.length === 0) return 'Select elements to remove';
  if (hasStartOrFinishSplitInSelection(features, allFeatures))
    return 'Cannot remove: selection contains a Start or Finish split';
  const labels = [...features].map(getFeatureLabel);
  if (labels.length === 1) return `Remove ${labels[0]} (Delete)`;
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1);
  if (labels.length <= 3) return `Remove ${rest.join(', ')} and ${last} (Delete)`;
  return `Remove ${rest[0]}, ${rest[1]} and ${labels.length - 2} more (Delete)`;
}

export function getMoveTitle(features: SelectedFeatures): string {
  if (features.length === 0) return 'Select elements to move';
  const labels = [...features].map(getFeatureLabel);
  if (labels.length === 1) return `Move ${labels[0]} (Drag)`;
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1);
  if (labels.length <= 3) return `Move ${rest.join(', ')} and ${last} (Drag)`;
  return `Move ${rest[0]}, ${rest[1]} and ${labels.length - 2} more (Drag)`;
}
